import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { collection, addDoc, updateDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuInfo, LuListPlus, LuLoaderCircle, LuPencil, LuSave, LuX } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import MultiSelect from '../../components/form/MultiSelect';
import { Field, SelectInput, TextArea, TextInput } from '../../components/form/FormControls';
import useTodoText from './todoText';
import { PRIORITIES, STATUSES } from './todoColumns';

const FORM_ID = 'todo-task-form';

/**
 * New task / edit task. Saving is the old form's: assignees, due date and,
 * for a new task mentioning "proje", one extra task per accounting-team
 * member grouped under `accountingGroup`. Remounted on every open (key).
 */
const AddTaskModal = ({ isOpen, onClose, onUpdate, employees = [], editingTask = null }) => {
  const { employeeData } = useUserProfile();
  const { label, language, statusLabel, priorityLabel } = useTodoText();
  const isEditing = !!editingTask;

  const [formData, setFormData] = useState({
    title: editingTask?.title || '',
    description: editingTask?.description || '',
    priority: editingTask?.priority || 'medium',
    status: editingTask?.status || 'pending',
    dueDate: editingTask?.dueDate
      ? (editingTask.dueDate.toDate ? editingTask.dueDate.toDate() : new Date(editingTask.dueDate))
          .toISOString().split('T')[0]
      : '',
  });

  const [selectedAssignees, setSelectedAssignees] = useState(
    editingTask?.assignees || []
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleAssignee = (emp) => {
    setSelectedAssignees(prev => {
      const exists = prev.find(a => a.id === emp.id);
      if (exists) {
        return prev.filter(a => a.id !== emp.id);
      }
      return [...prev, { id: emp.id, name: `${emp.eName} ${emp.eSurname}` }];
    });
  };

  const removeAssignee = (empId) => {
    setSelectedAssignees(prev => prev.filter(a => a.id !== empId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error(label('toast.titleRequired'));
      return;
    }

    if (selectedAssignees.length === 0) {
      toast.error(label('toast.assigneeRequired'));
      return;
    }

    setIsSaving(true);

    try {
      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        status: formData.status,
        assignees: selectedAssignees,
        assigneeName: selectedAssignees.map(a => a.name).join(', '),
        assigneeId: selectedAssignees.length === 1 ? selectedAssignees[0].id : null,
        dueDate: formData.dueDate ? Timestamp.fromDate(new Date(formData.dueDate)) : null,
        updatedAt: serverTimestamp()
      };

      if (isEditing) {
        const taskRef = doc(db, 'Tasks', editingTask.id);
        await updateDoc(taskRef, taskData);
        toast.success(label('toast.updated'));
      } else {
        taskData.createdAt = serverTimestamp();
        taskData.createdBy = employeeData
          ? `${employeeData.eName || ''} ${employeeData.eSurname || ''}`.trim()
          : 'Admin';

        // Ana görevi oluştur
        const mainTaskRef = await addDoc(collection(db, 'Tasks'), taskData);

        // Görev başlığında "proje" geçiyorsa muhasebe ekibine otomatik görev oluştur
        const isProjectTask = formData.title.toLowerCase().includes('proje') ||
                             formData.description.toLowerCase().includes('proje');

        if (isProjectTask) {
          // Muhasebe ekibi üyeleri
          const accountingTeam = [
            { name: 'AYGÜL DAĞ CEYLAN', email: 'aygul.dag@habcare.com.tr' },
            { name: 'ÖZGE YIKIMLAR', email: 'ozge.yikimlar@habcare.com.tr' },
            { name: 'MELİSA ŞENER', email: 'melisa.sener@habcare.com.tr' }
          ];

          // Muhasebe ekibi üyelerini employees listesinden bul
          const accountingEmployees = employees.filter(emp =>
            accountingTeam.some(member =>
              `${emp.eName} ${emp.eSurname}`.toUpperCase().includes(member.name.toUpperCase()) ||
              emp.eMail?.toLowerCase().includes(member.email.toLowerCase())
            )
          );

          // Her muhasebe üyesi için ayrı görev oluştur
          const accountingTaskPromises = accountingEmployees.map(async (emp) => {
            const accountingTaskData = {
              title: `${formData.title.trim()} - Muhasebe İşlemleri`,
              description: `${formData.description.trim()}\n\nBu görev yeni proje için muhasebe işlemlerini kapsamaktadır.`,
              priority: formData.priority,
              status: 'pending',
              assignees: [{ id: emp.id, name: `${emp.eName} ${emp.eSurname}` }],
              assigneeName: `${emp.eName} ${emp.eSurname}`,
              assigneeId: emp.id,
              dueDate: formData.dueDate ? Timestamp.fromDate(new Date(formData.dueDate)) : null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              createdBy: employeeData
                ? `${employeeData.eName || ''} ${employeeData.eSurname || ''}`.trim()
                : 'Admin',
              parentTaskId: mainTaskRef.id, // Ana görevle ilişkilendir
              isAccountingTask: true, // Muhasebe görevi olduğunu işaretle
              accountingGroup: `${formData.title.trim()}_${Date.now()}` // Grup kimliği
            };

            return addDoc(collection(db, 'Tasks'), accountingTaskData);
          });

          await Promise.all(accountingTaskPromises);

          toast.success(label('toast.createdWithAccounting', { count: accountingEmployees.length }));
        } else {
          toast.success(label('toast.created'));
        }
      }

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(label('toast.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const mentionsProject = !isEditing && `${formData.title} ${formData.description}`.toLowerCase().includes('proje');
  const employeeOptions = employees.map((emp) => ({ key: emp.id, label: `${emp.eName} ${emp.eSurname}`.trim(), hint: emp.eMail || undefined }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      dismissible={!isSaving}
      title={isEditing ? label('form.editTitle') : label('form.newTitle')}
      subtitle={label('form.subtitle')}
      closeLabel={label('form.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          {isEditing ? <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuListPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>{label('form.cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={isSaving}>
            {isSaving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isSaving ? label('form.saving') : isEditing ? label('form.update') : label('form.create')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="tw-space-y-4">
        <Field label={label('form.title')} htmlFor="todo-title" required>
          <TextInput id="todo-title" name="title" value={formData.title} onChange={handleChange} placeholder={label('form.titlePlaceholder')} autoFocus />
        </Field>

        <Field label={label('form.description')} htmlFor="todo-description">
          <TextArea id="todo-description" name="description" rows={4} value={formData.description} onChange={handleChange} placeholder={label('form.descriptionPlaceholder')} />
        </Field>

        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('form.priority')}>
            <SegmentedControl
              size="sm"
              layoutId="todo-form-priority"
              label={label('form.priority')}
              value={formData.priority}
              onChange={(value) => setFormData((prev) => ({ ...prev, priority: value }))}
              options={[...PRIORITIES].reverse().map((value) => ({ value, label: priorityLabel(value) }))}
            />
          </Field>
          <Field label={label('form.dueDate')} htmlFor="todo-due">
            <TextInput id="todo-due" type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} />
          </Field>
        </div>

        {isEditing && (
          <Field label={label('form.status')} htmlFor="todo-status">
            <SelectInput
              id="todo-status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={STATUSES.map((value) => ({ value, label: statusLabel(value) }))}
            />
          </Field>
        )}

        <Field label={label('form.assignees')} htmlFor="todo-assignees" required>
          <MultiSelect
            id="todo-assignees"
            options={employeeOptions}
            isSelected={(option) => selectedAssignees.some((a) => a.id === option.key)}
            onToggle={(option) => {
              const emp = employees.find((item) => item.id === option.key);
              if (emp) toggleAssignee(emp);
            }}
            summary={selectedAssignees.length > 0 ? label('form.selected', { count: selectedAssignees.length }) : undefined}
            placeholder={label('form.assigneesPlaceholder')}
            searchPlaceholder={label('form.assigneeSearch')}
            emptyText={label('form.noResults')}
          />
          {selectedAssignees.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-pt-1">
              {selectedAssignees.map((a) => (
                <span key={a.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-teal-500/10 tw-py-1 tw-pl-2.5 tw-pr-1 tw-text-xs tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200">
                  {a.name}
                  <button type="button" onClick={() => removeAssignee(a.id)} aria-label={label('form.remove', { name: a.name })} className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-transparent hover:tw-bg-teal-500/20">
                    <LuX aria-hidden="true" className="tw-h-3 tw-w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>

        {mentionsProject && (
          <p className="tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-border tw-border-amber-400/40 tw-bg-amber-50 tw-px-3.5 tw-py-3 tw-text-sm tw-text-amber-900 dark:tw-border-amber-400/25 dark:tw-bg-amber-400/10 dark:tw-text-amber-100">
            <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            {label('form.accountingNote')}
          </p>
        )}
      </form>
    </Modal>
  );
};

AddTaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  employees: PropTypes.arrayOf(PropTypes.object),
  editingTask: PropTypes.object,
};

export default AddTaskModal;
